import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Logo } from './Logo';
import { ThemeToggle } from './ThemeToggle';

export function Footer({ internal = false }: { internal?: boolean }) {
  return (
    <footer className="border-t border-border/60">
      <div className="mx-auto w-full max-w-5xl px-4 py-6 sm:px-6">
        {internal && (
          <p className="mb-3 inline-flex items-center gap-2 rounded-full border border-warn/40 bg-warn/10 px-3 py-1 text-xs font-medium text-warn">
            ⚠ Confidential — for internal use only. Not for external distribution.
          </p>
        )}
        <p className="text-xs leading-relaxed text-muted">
          © 2026 Knocking Inc. All rights reserved. ·{' '}
          <a className="underline decoration-border underline-offset-2 transition-colors hover:text-foreground" href="https://www.knocking.com/privacypolicy" target="_blank" rel="noreferrer">
            Privacy Policy
          </a>{' '}
          ·{' '}
          <a className="underline decoration-border underline-offset-2 transition-colors hover:text-foreground" href="https://knocking.com" target="_blank" rel="noreferrer">
            knocking.com
          </a>
        </p>
        {!internal && (
          <p className="mt-1 text-xs leading-relaxed text-muted">
            Forms on this site are powered by PandaDoc; submissions are governed by the{' '}
            <a className="underline decoration-border underline-offset-2 transition-colors hover:text-foreground" href="https://www.pandadoc.com/privacy-notice/" target="_blank" rel="noreferrer">
              PandaDoc privacy notice
            </a>
            .
          </p>
        )}
      </div>
    </footer>
  );
}

export function PublicLayout({
  children,
  backTo,
}: {
  children: ReactNode;
  /** Compact breadcrumb-style back link rendered in the header next to the logo. */
  backTo?: { label: string; to: string };
}) {
  return (
    <div className="flex min-h-screen flex-col">
      <header className="border-b border-border/60">
        <div className="mx-auto flex w-full max-w-5xl items-center justify-between px-4 py-4 sm:px-6">
          <div className="flex min-w-0 items-center gap-3">
            <Link to="/" aria-label="Knocking Forms home" className="shrink-0 rounded-sm">
              <Logo />
            </Link>
            {backTo && (
              <>
                <span aria-hidden className="h-5 w-px shrink-0 bg-border" />
                <Link
                  to={backTo.to}
                  className="inline-flex min-w-0 items-center gap-0.5 truncate rounded-sm text-sm text-muted transition-colors hover:text-brand"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden className="shrink-0">
                    <path d="m15 18-6-6 6-6" />
                  </svg>
                  <span className="truncate">{backTo.label}</span>
                </Link>
              </>
            )}
          </div>
          <ThemeToggle />
        </div>
      </header>
      <main className="flex flex-1 flex-col">{children}</main>
      <Footer />
    </div>
  );
}
